import { MAX_BALLS, MAX_TIMED, PASS_GROUP, PICKUP_SCORE, POWER_DURATION, SPEED_GROUP, TIMED_IDS, WIDTH_GROUP } from './constants';
import type { PowerState, PowerUpId, TimedPowerId } from './types';

export const POWER_WEIGHTS: { id: PowerUpId; weight: number }[] = [
  { id: 'bigPaddle', weight: 18 },
  { id: 'smallPaddle', weight: 8 },
  { id: 'slowBall', weight: 14 },
  { id: 'fastBall', weight: 8 },
  { id: 'multiBall', weight: 6 },
  { id: 'fireBall', weight: 5 },
  { id: 'extraLife', weight: 4 },
  { id: 'shield', weight: 10 },
  { id: 'magnet', weight: 9 },
  { id: 'laser', weight: 7 },
  { id: 'piercing', weight: 5 },
  { id: 'brickBlast', weight: 8 },
];

export type CollectResult =
  | { kind: 'timed'; state: PowerState }
  | { kind: 'score'; state: PowerState; score: number }
  | { kind: 'extraLife'; state: PowerState }
  | { kind: 'brickBlast'; state: PowerState }
  | { kind: 'multiBall'; state: PowerState }
  | { kind: 'shield'; state: PowerState };

export function emptyPowerState(): PowerState {
  return { timed: {}, shield: false };
}

export function timedCount(state: PowerState): number {
  return TIMED_IDS.reduce((n, id) => n + ((state.timed[id] ?? 0) > 0 ? 1 : 0), 0);
}

function clearGroup(timed: PowerState['timed'], group: TimedPowerId[], keep: TimedPowerId): PowerState['timed'] {
  const next = { ...timed };
  for (const id of group) {
    if (id !== keep) delete next[id];
  }
  return next;
}

function isTimed(id: PowerUpId): id is TimedPowerId {
  return (TIMED_IDS as string[]).includes(id);
}

export function collectPower(state: PowerState, id: PowerUpId): CollectResult {
  if (id === 'extraLife') return { kind: 'extraLife', state };
  if (id === 'brickBlast') return { kind: 'brickBlast', state };
  if (id === 'multiBall') return { kind: 'multiBall', state };
  if (id === 'shield') return { kind: 'shield', state: { ...state, shield: true } };

  if (!isTimed(id)) return { kind: 'score', state, score: PICKUP_SCORE };

  const already = (state.timed[id] ?? 0) > 0;
  const replacing =
    (WIDTH_GROUP.includes(id) && WIDTH_GROUP.some((g) => hasTimed(state, g))) ||
    (SPEED_GROUP.includes(id) && SPEED_GROUP.some((g) => hasTimed(state, g))) ||
    (PASS_GROUP.includes(id) && PASS_GROUP.some((g) => hasTimed(state, g)));
  if (!already && !replacing && timedCount(state) >= MAX_TIMED) {
    return { kind: 'score', state, score: PICKUP_SCORE };
  }

  let timed = { ...state.timed, [id]: POWER_DURATION[id] };
  if (WIDTH_GROUP.includes(id)) timed = clearGroup(timed, WIDTH_GROUP, id);
  if (SPEED_GROUP.includes(id)) timed = clearGroup(timed, SPEED_GROUP, id);
  if (PASS_GROUP.includes(id)) {
    const keep = id === 'fireBall' || (id === 'piercing' && !((timed.fireBall ?? 0) > 0)) ? id : 'fireBall';
    timed = clearGroup(timed, PASS_GROUP, keep);
    if (id === 'piercing' && (state.timed.fireBall ?? 0) > 0) {
      timed.fireBall = state.timed.fireBall;
    }
  }
  return { kind: 'timed', state: { ...state, timed } };
}

export function tickPowers(state: PowerState, dtMs: number): PowerState {
  const timed: PowerState['timed'] = {};
  for (const id of TIMED_IDS) {
    const left = (state.timed[id] ?? 0) - dtMs;
    if (left > 0) timed[id] = left;
  }
  return { timed, shield: state.shield };
}

export function extraBallsToSpawn(current: number, want = 2, cap = MAX_BALLS): number {
  return Math.max(0, Math.min(want, cap - current));
}

export function rollPowerId(rand = Math.random): PowerUpId {
  const total = POWER_WEIGHTS.reduce((s, p) => s + p.weight, 0);
  let n = rand() * total;
  for (const p of POWER_WEIGHTS) {
    n -= p.weight;
    if (n <= 0) return p.id;
  }
  return 'bigPaddle';
}

export function shouldDrop(isBonus: boolean, falling: number, mul = 1, rand = Math.random): boolean {
  if (falling >= 2) return false;
  const rate = (isBonus ? 0.28 : 0.06) * mul;
  return rand() < rate;
}

export function hasTimed(state: PowerState, id: TimedPowerId): boolean {
  return (state.timed[id] ?? 0) > 0;
}

export function paddleWidthMul(state: PowerState): number {
  if (hasTimed(state, 'bigPaddle')) return 1.45;
  if (hasTimed(state, 'smallPaddle')) return 0.68;
  return 1;
}

export function ballSpeedMul(state: PowerState): number {
  if (hasTimed(state, 'slowBall')) return 0.72;
  if (hasTimed(state, 'fastBall')) return 1.32;
  return 1;
}
